import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFilePdf, faImage, faDownload, faEye, faExclamationTriangle, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { IAttachment } from '../../../interfaces/domain';
import AttachmentsService from '../../../services/AttachmentsService';
import Table from '../../../components/UI/Table/Table';
import Button, { EButtonSize, EButtonColor } from '../../../components/UI/Button/Button';
import Spinner from '../../../components/UI/Spinner/Spinner';
import IFrame from '../../../components/UI/IFrame/IFrame';

interface IProps {
  attachments?: IAttachment[];
  offerRequestId?: number | string;
  customerEmail?: string | null;
}

const getMimeTypeFromFilename = (fileName?: string): string => {
  if (!fileName) return 'application/octet-stream';
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  if (lower.endsWith('.pdf')) return 'application/pdf';
  return 'application/octet-stream';
};

const getContentTypeIcon = (contentType?: string, clientName?: string) => {
  const type = contentType || getMimeTypeFromFilename(clientName);
  if (type.startsWith('image/')) {
    return <FontAwesomeIcon icon={faImage} style={{ color: '#007bff' }} />;
  }
  if (type === 'application/pdf') {
    return <FontAwesomeIcon icon={faFilePdf} style={{ color: '#dc3545' }} />;
  }
  return <FontAwesomeIcon icon={faFile} style={{ color: '#6c757d' }} />;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getAttachmentId = (att: IAttachment): string => {
  return att.id || att.serverName || '';
};

const AttachmentsList: React.FC<IProps> = ({ attachments, offerRequestId, customerEmail }) => {
  const [activeAttachment, setActiveAttachment] = useState<IAttachment | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());

  const fetchBlob = useCallback(async (attachment: IAttachment) => {
    const attId = getAttachmentId(attachment);
    if (!attId) return;
    const activeId = activeAttachment ? getAttachmentId(activeAttachment) : null;
    if (activeId === attId && blob) return;

    try {
      setLoadingId(attId);
      setError(null);
      setActiveAttachment(attachment);
      const targetOfferRequestId = attachment.offerRequestId || offerRequestId;
      const data = await AttachmentsService.downloadAttachment(attId, targetOfferRequestId);
      const targetMimeType = attachment.contentType || getMimeTypeFromFilename(attachment.clientName || attachment.serverName);
      const typedBlob = data.type && data.type !== 'application/octet-stream'
        ? data
        : new Blob([data], { type: targetMimeType });
      setBlob(typedBlob);
    } catch (err: any) {
      console.error('Failed to download attachment', err);
      setFailedIds((prev) => new Set(prev).add(attId));
      setError('Liitetiedosto ei ole saatavilla tallennustilassa.');
      setBlob(null);
    } finally {
      setLoadingId(null);
    }
  }, [activeAttachment, blob, offerRequestId]);

  useEffect(() => {
    if (attachments && attachments.length > 0 && !activeAttachment) {
      fetchBlob(attachments[0]);
    }
  }, [attachments, activeAttachment, fetchBlob]);

  const handleDownloadDirect = async (e: React.MouseEvent, attachment: IAttachment) => {
    e.stopPropagation();
    const attId = getAttachmentId(attachment);
    if (!attId) return;
    try {
      setLoadingId(attId);
      const targetOfferRequestId = attachment.offerRequestId || offerRequestId;
      const downloadedBlob = await AttachmentsService.downloadAttachment(attId, targetOfferRequestId);
      const targetMimeType = attachment.contentType || getMimeTypeFromFilename(attachment.clientName || attachment.serverName);
      const typedBlob = downloadedBlob.type && downloadedBlob.type !== 'application/octet-stream'
        ? downloadedBlob
        : new Blob([downloadedBlob], { type: targetMimeType });
      const url = URL.createObjectURL(typedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.clientName || 'liite';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error', err);
      setFailedIds((prev) => new Set(prev).add(attId));
      setError('Liitetiedosto ei ole saatavilla tallennustilassa.');
      setActiveAttachment(attachment);
      setBlob(null);
    } finally {
      setLoadingId(null);
    }
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const hasMissingFiles = failedIds.size > 0;

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff', padding: '1.25rem', marginTop: '1.5rem' }}>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Liitteet ({attachments.length})
      </h3>

      {hasMissingFiles && (
        <div style={{
          padding: '0.85rem 1rem',
          backgroundColor: '#fff8e1',
          border: '1px solid #ffe082',
          borderRadius: '6px',
          color: '#795548',
          marginBottom: '1rem',
          fontSize: '0.9rem',
          lineHeight: '1.5'
        }}>
          <div style={{ fontWeight: 'bold', color: '#b78103', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Huomio: Kaikkia liitteitä ei ole saatavilla suoraan järjestelmästä
          </div>
          <div>
            Tiedoston tallennuksessa tapahtui häiriö järjestelmäsiirron aikana. <strong>Ongelma on korjattu ja kaikki uudet liitteet tallentuvat jatkossa normaalisti.</strong>
          </div>
          <div style={{ marginTop: '0.35rem' }}>
            Olethan tarvittaessa yhteydessä asiakkaaseen {customerEmail ? <strong>({customerEmail})</strong> : ''} pyytääksesi tämän vanhan liitteen lähetystä suoraan sähköpostiisi.
          </div>
        </div>
      )}

      {error && !hasMissingFiles && (
        <div style={{ color: '#dc3545', padding: '0.5rem', backgroundColor: '#f8d7da', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      <Table hover style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Tiedosto</th>
            <th>Koko</th>
            <th style={{ textAlign: 'right' }}>Toiminnot</th>
          </tr>
        </thead>
        <tbody>
          {attachments.map((att) => {
            const attId = getAttachmentId(att);
            const activeId = activeAttachment ? getAttachmentId(activeAttachment) : null;
            const isActive = activeId === attId;
            const isLoading = loadingId === attId;
            const isMissing = failedIds.has(attId);

            return (
              <tr
                key={attId}
                style={{
                  cursor: 'pointer',
                  backgroundColor: isActive ? '#e9ecef' : (isMissing ? '#fffdf7' : undefined),
                  fontWeight: isActive ? 'bold' : 'normal',
                }}
                onClick={() => fetchBlob(att)}
              >
                <td style={{ wordBreak: 'break-word', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {getContentTypeIcon(att.contentType, att.clientName)}
                  <span>{att.clientName}</span>
                  {isMissing && (
                    <span style={{
                      fontSize: '0.75rem',
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      border: '1px solid #ffeeba',
                      padding: '0.15rem 0.4rem',
                      borderRadius: '4px',
                      fontWeight: 'normal',
                    }}>
                      Ei saatavilla (pyydä sähköpostitse)
                    </span>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem', color: '#666' }}>
                  {formatFileSize(att.size)}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'inline-flex', gap: '0.25rem' }}>
                    <Button
                      size={EButtonSize.SMALL}
                      color={isActive ? EButtonColor.PRIMARY : EButtonColor.SECONDARY}
                      title="Esikatsele"
                      onClick={(e) => {
                        e.stopPropagation();
                        fetchBlob(att);
                      }}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      size={EButtonSize.SMALL}
                      color={EButtonColor.PRIMARY}
                      title="Lataa tiedosto"
                      onClick={(e) => handleDownloadDirect(e, att)}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Preview Section */}
      {activeAttachment && (
        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <h4 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1rem', color: '#444' }}>
            Esikatselu: {activeAttachment.clientName}
          </h4>
          {loadingId === getAttachmentId(activeAttachment) ? (
            <Spinner />
          ) : blob ? (
            <IFrame blob={blob} title={activeAttachment.clientName} />
          ) : (
            <div style={{
              backgroundColor: '#f8f9fa',
              border: '1px dashed #ced4da',
              borderRadius: '6px',
              padding: '1.5rem',
              textAlign: 'center',
              color: '#495057'
            }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: '1.8rem', color: '#6c757d', marginBottom: '0.5rem' }} />
              <h5 style={{ margin: '0.5rem 0', fontWeight: 600 }}>Liitetiedosto ei ole saatavilla tallennustilassa</h5>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#6c757d', maxWidth: '540px', marginInline: 'auto' }}>
                Liitteen <strong>{activeAttachment.clientName}</strong> tallennus on jäänyt vajaaksi järjestelmäsiirron aikana. Ongelma on korjattu ja kaikki uudet liitteet toimivat normaalisti.
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#495057', maxWidth: '540px', marginInline: 'auto' }}>
                Pyydäthän tarvittaessa asiakasta {customerEmail ? <strong>({customerEmail})</strong> : ''} toimittamaan tämän liitteen suoraan sähköpostiisi.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentsList;
